'use client';

import { useState, useEffect, useRef } from 'react';
import Header from '@/components/common/Header';
import DrillConfigurationInteractive from './components/DrillConfigurationInteractive';

const CURRENT_COMMUNITY_CODE = 'XE2026';
const STORAGE_KEY = 'community_access_token';

export default function DrillConfigurationPage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === CURRENT_COMMUNITY_CODE) {
      setIsUnlocked(true);
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated && !isUnlocked && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isHydrated, isUnlocked]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value.toUpperCase());
    if (error) setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue === CURRENT_COMMUNITY_CODE) {
      localStorage.setItem(STORAGE_KEY, CURRENT_COMMUNITY_CODE);
      setIsUnlocked(true);
    } else {
      setError('Código incorrecto o caducado');
      setInputValue('');
    }
  };

  if (!isHydrated) return null;

  return (
    <main className="min-h-screen bg-background">
      <Header />

      {/* ACCESS GATE OVERLAY */}
      {!isUnlocked &&
      <div
        className="page-transition-wrap fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        
          {/* Cinematic background image */}
          <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "https://images.unsplash.com/photo-1636890205154-183fc9a500d4",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(50%) contrast(0.90) brightness(0.65)'
          }} />
        
          {/* Deep dark overlay */}
          <div
          className="absolute inset-0 z-0"
          style={{ background: 'rgba(12,12,12,0.85)' }} />
        

          {/* Gate container */}
          <div
          className="relative z-10 w-full max-w-sm"
          style={{
            background: '#141414',
            border: '1px solid #2A2A2A',
            borderRadius: 0,
            padding: '48px 40px 44px'
          }}>
          
            {/* Decorative top rule */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px flex-1" style={{ background: 'rgba(194,178,153,0.15)' }} />
              <div className="w-1 h-1" style={{ background: 'rgba(194,178,153,0.35)' }} />
              <div className="h-px flex-1" style={{ background: 'rgba(194,178,153,0.15)' }} />
            </div>

            {/* Title */}
            <h1
            className="text-center mb-2"
            style={{
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: '2rem',
              letterSpacing: '0.06em',
              color: '#FAF7F2',
              lineHeight: 1.2
            }}>
            
              Acceso Exclusivo
            </h1>

            {/* Subtitle */}
            <p
            className="text-center mb-10"
            style={{
              fontSize: '0.65rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#4A4540',
              fontWeight: 500
            }}>
            
              Comunidad · Xavier Evan
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Input field */}
              <div className="space-y-2">
                <label
                htmlFor="access-code"
                style={{
                  display: 'block',
                  fontSize: '0.6rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#5A5550',
                  fontWeight: 500
                }}>
                
                  Código de Acceso
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                  ref={inputRef}
                  id="access-code"
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="· · · · · ·"
                  className="access-gate-input"
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,250,242,0.12)',
                    borderRadius: 0,
                    padding: '8px 0',
                    color: '#FAF7F2',
                    fontSize: '0.9rem',
                    letterSpacing: '0.25em',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    outline: 'none',
                    transition: 'border-color 0.25s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderBottomColor = '#C2B299';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderBottomColor = 'rgba(255,250,242,0.12)';
                  }} />
                
                </div>

                {/* Error message */}
                <div
                style={{
                  minHeight: '18px',
                  fontSize: '0.65rem',
                  letterSpacing: '0.08em',
                  color: error ? 'rgba(194,120,100,0.85)' : 'transparent',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  paddingTop: '4px'
                }}>
                
                  {error || '\u00A0'}
                </div>
              </div>

              {/* Submit button */}
              <button
              type="submit"
              className="w-full"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,250,242,0.25)',
                borderRadius: 0,
                padding: '12px 24px',
                color: '#FAF7F2',
                fontSize: '0.65rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'border-color 0.2s ease, color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(194,178,153,0.5)';
                e.currentTarget.style.color = '#C2B299';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,250,242,0.25)';
                e.currentTarget.style.color = '#FAF7F2';
              }}>
              
                Verificar Acceso
              </button>
            </form>

            {/* Bottom decorative rule */}
            <div className="flex items-center justify-center gap-3 mt-10">
              <div className="h-px flex-1" style={{ background: 'rgba(194,178,153,0.08)' }} />
              <div className="w-1 h-1" style={{ background: 'rgba(194,178,153,0.2)' }} />
              <div className="h-px flex-1" style={{ background: 'rgba(194,178,153,0.08)' }} />
            </div>
          </div>
        </div>
      }

      {/* Main app content — rendered beneath the overlay */}
      <DrillConfigurationInteractive />
    </main>);

}